import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { OPEN_LIBRARY_SECTION_OPTIONS } from "@/lib/openLibrarySectionCatalog.mjs";

function currentDestinationKey(volume) {
  if (!volume) return "editorial";
  if (volume.product_id) return `product:${volume.product_id}`;
  if (volume.slug === "open-library-author-intro") return "open_library_author_intro";
  if (volume.slug === "open-library") return "open_library";
  return "editorial";
}

export function PublicationDestinationV2({ materialId, volume }) {
  const [items, setItems] = useState([]);
  const [selectedKey, setSelectedKey] = useState(currentDestinationKey(volume));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState(null);
  const [sectionBusy, setSectionBusy] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    setSelectedKey(currentDestinationKey(volume));
  }, [volume?.id, volume?.product_id, volume?.slug]);

  useEffect(() => {
    if (volume?.slug !== "open-library" || !materialId) return;
    let active = true;
    api.get(`/admin/editorial/materials/${encodeURIComponent(materialId)}`)
      .then(({data}) => { if (active) setSection(data.open_library_section || ""); })
      .catch((e) => { if (active) toast.error(formatApiError(e)); });
    return () => { active = false; };
  }, [materialId, volume?.slug]);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/editorial/publication-destinations")
      .then(({ data }) => {
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = items.find((item) => item.key === selectedKey)
    || {
      key: selectedKey,
      title: volume?.title || "Редакция",
      subtitle: volume?.product_id ? "Продукт" : "Внутренний материал",
    };

  const availableItems = useMemo(() => items.filter((item) =>
    item.key !== "open_library_author_intro" || volume?.slug === "open-library-author-intro"
  ), [items, volume?.slug]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableItems;
    return availableItems.filter((item) =>
      `${item.title || ""} ${item.subtitle || ""}`.toLowerCase().includes(q)
    );
  }, [availableItems, query]);

  const choose = async (item) => {
    if (!item || item.key === selectedKey || busy) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/editorial/materials/${materialId}/destination`,
        { destination_key: item.key }
      );
      setSelectedKey(data.destination_key || item.key);
      setOpen(false);
      setQuery("");
      window.dispatchEvent(new CustomEvent("editorial-v2-destination-changed", {
        detail: {
          materialId,
          material: data.material,
          volume: data.volume,
          destinationKey: data.destination_key || item.key,
        },
      }));
      toast.success(`Место публикации: ${item.title}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const chooseSection = async (next) => {
    if (!next || sectionBusy || next === section) return;
    setSectionBusy(true);
    try {
      const {data} = await api.post(
        `/admin/editorial/materials/${encodeURIComponent(materialId)}/open-library-section`,
        { section: next }
      );
      setSection(data.open_library_section || next);
      window.dispatchEvent(new CustomEvent("editorial-v2-section-changed", {
        detail: { materialId, material: data },
      }));
      toast.success("Раздел сохранён. Перед публикацией подтвердите предпросмотр.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSectionBusy(false); }
  };

  return (
    <div ref={rootRef} className="relative rounded-2xl border border-espresso/10 bg-white/70 px-4 py-3"
      data-testid="editorial-v2-publication-destination">
      <div className="text-[10px] uppercase tracking-widest text-espresso/55">Где будет опубликовано</div>
      <button type="button" onClick={() => setOpen((v) => !v)} disabled={busy}
        className="mt-2 flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-espresso/15 bg-[#FBF5EA] px-3 py-2 text-left disabled:opacity-60"
        data-testid="editorial-v2-publication-destination-trigger">
        <div className="min-w-0">
          <div className="truncate font-serif text-[16px] text-espresso">{selected.title}</div>
          {selected.subtitle && <div className="mt-0.5 text-[11px] text-espresso/50">{selected.subtitle}</div>}
        </div>
        <ChevronDown size={16} className={open ? "shrink-0 rotate-180 text-espresso/50" : "shrink-0 text-espresso/50"} />
      </button>

      {volume?.slug === "open-library" && (
        <div className="mt-3" data-testid="editorial-v2-library-section">
          <label htmlFor="editorial-library-section-select" className="mb-1 block text-xs text-espresso/75">
            Раздел Открытой библиотеки
          </label>
          <select id="editorial-library-section-select"
            className="min-h-11 w-full rounded-xl border border-espresso/20 bg-[#FBF5EA] px-3 text-sm"
            value={section ?? ""} disabled={sectionBusy} onChange={(e) => chooseSection(e.target.value)}>
            <option value="">Выберите раздел</option>
            {OPEN_LIBRARY_SECTION_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>{option.title}</option>
            ))}
          </select>
        </div>
      )}
      {open && (
        <div className="absolute left-4 right-4 top-[92px] z-[80] overflow-hidden rounded-2xl border border-[#E8DBC6] bg-[#FFFDF8]"
          style={{ boxShadow: "0 18px 42px -18px rgba(58,43,31,0.34)" }}>
          <div className="border-b border-[#EFE4D0] p-2">
            <div className="flex items-center gap-2 rounded-xl bg-[#FBF5EA] px-3">
              <Search size={14} className="text-espresso/40" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Найти место публикации"
                className="h-10 w-full bg-transparent text-[13px] text-espresso outline-none"
                data-testid="editorial-v2-publication-destination-search" />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-1.5">
            {filtered.map((item) => {
              const active = item.key === selectedKey;
              return (
                <button key={item.key} type="button" onClick={() => choose(item)}
                  className={active
                    ? "flex w-full items-center justify-between gap-3 rounded-xl bg-[#F3E8D7] px-3 py-2.5 text-left"
                    : "flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[#FBF5EA]"}
                  data-testid={`editorial-v2-publication-destination-option-${item.key.replace(/[^a-zA-Z0-9_-]/g, "-")}`}>
                  <div className="min-w-0">
                    <div className="truncate font-serif text-[15px] text-espresso">{item.title}</div>
                    {item.subtitle && <div className="mt-0.5 text-[10px] uppercase tracking-wider text-espresso/45">{item.subtitle}</div>}
                  </div>
                  {active && <Check size={16} className="shrink-0 text-espresso" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-5 text-center text-xs text-espresso/50">Ничего не найдено</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
